import socketIO from "socket.io-client";
import { Match, Show, Switch, createMemo, createSignal } from "solid-js";

const socket = socketIO({
  transports: ["websocket", "polling"],
  reconnection: true,
});

type ConnectionStatus = 'connecting' | 'connected' | 'reconnecting' | 'unauthorized';
type CellUpdate = { project_id: number; page: string; key: string; value: string };
type PendingUpdate = { payload: CellUpdate; version: number; sending: boolean };

const [connectionStatus, setConnectionStatus] = createSignal<ConnectionStatus>(
  socket.connected ? 'connected' : 'connecting'
);
const [pendingCount, setPendingCount] = createSignal(0);
const [syncError, setSyncError] = createSignal(false);
const pendingUpdates = new Map<string, PendingUpdate>();
let authenticated = false;
let nextVersion = 0;

const updatePendingCount = () => setPendingCount(pendingUpdates.size);
const updateId = (payload: CellUpdate) => `${payload.project_id}\n${payload.page}\n${payload.key}`;

const flushUpdate = (id: string) => {
  const update = pendingUpdates.get(id);
  if (!update || update.sending || !socket.connected || !authenticated) return;

  update.sending = true;
  const sentVersion = update.version;
  socket.timeout(8000).emit('project-update-cell', update.payload, (error: unknown, response?: { saved?: boolean }) => {
    const current = pendingUpdates.get(id);
    if (!current) return;

    if (!error && response?.saved) {
      setSyncError(false);
      if (current.version === sentVersion) {
        pendingUpdates.delete(id);
        updatePendingCount();
      } else {
        current.sending = false;
        flushUpdate(id);
      }
      return;
    }

    current.sending = false;
    setSyncError(true);
    window.setTimeout(() => {
      if (pendingUpdates.get(id) === current) flushUpdate(id);
    }, 2000);
  });
};

const flushPendingUpdates = () => pendingUpdates.forEach((_update, id) => flushUpdate(id));

export const saveCell = (payload: CellUpdate) => {
  const id = updateId(payload);
  const current = pendingUpdates.get(id);
  pendingUpdates.set(id, { payload, version: ++nextVersion, sending: current?.sending ?? false });
  setSyncError(false);
  updatePendingCount();
  flushUpdate(id);
};

socket.on('connect', () => setConnectionStatus('connected'));
socket.on('authenticated', () => {
  authenticated = true;
  setConnectionStatus('connected');
  flushPendingUpdates();
});
socket.on('disconnect', () => {
  authenticated = false;
  setConnectionStatus('reconnecting');
  pendingUpdates.forEach((update) => { update.sending = false; });
});
socket.io.on('reconnect_attempt', () => setConnectionStatus('reconnecting'));
socket.on('connect_error', () => setConnectionStatus('reconnecting'));
socket.on('unauthorized', () => {
  authenticated = false;
  setConnectionStatus('unauthorized');
});

window.addEventListener('beforeunload', (event) => {
  if (pendingUpdates.size === 0) return;
  event.preventDefault();
  event.returnValue = '';
});

export default socket;

export const SyncStatus = () => {
  const state = createMemo(() => {
    if (pendingCount() > 0 && (connectionStatus() !== 'connected' || syncError())) return 'waiting';
    if (pendingCount() > 0) return 'syncing';
    return 'saved';
  });

  return (
    <div class={`sync-status sync-status-${state()}`} aria-live="polite">
      <Switch>
        <Match when={state() === 'syncing'}>
          <span class="spinner-border" aria-hidden="true"></span><span>Synchronisation…</span>
        </Match>
        <Match when={state() === 'waiting'}>
          <i class="bi bi-cloud-slash" aria-hidden="true"></i><span>Modifications non synchronisées</span>
        </Match>
        <Match when={state() === 'saved'}>
          <i class="bi bi-cloud-check" aria-hidden="true"></i><span>Enregistré</span>
        </Match>
      </Switch>
    </div>
  );
};

export const SocketStatus = () => {
  const reconnectLogin = () => {
    localStorage.setItem('next', location.href);
    location.href = '/login';
  };

  return (
    <Show when={connectionStatus() === 'reconnecting' || connectionStatus() === 'unauthorized'}>
      <div class="socket-toast" role="alert" aria-live="assertive">
        <Show when={connectionStatus() === 'reconnecting'} fallback={
          <>
            <i class="bi bi-exclamation-triangle-fill" aria-hidden="true"></i>
            <span>Votre session a expiré. Les modifications ne peuvent plus être enregistrées.</span>
            <button class="btn btn-sm btn-danger" onClick={reconnectLogin}>Se reconnecter</button>
          </>
        }>
          <div class="spinner-border spinner-border-sm text-danger" role="status">
            <span class="visually-hidden">Reconnexion…</span>
          </div>
          <span>Connexion au serveur perdue — reconnexion automatique en cours…</span>
        </Show>
      </div>
    </Show>
  );
};
